export type WorkspacePlan = 'free' | 'source' | 'managed';

/** The limited Free plan, not every subscription Stigg prices as FREE. */
export const isLimitedFreePlan = (planId: string | undefined) =>
  planId === 'plan-finlay-free';

export interface WorkspacePlanState {
  plan: WorkspacePlan | undefined;
  isLoading: boolean;
  error?: Error;
  /**
   * Whether the plan includes Finlay outreach and AI pre-screening, from
   * Stigg. Undefined when billing is off or Stigg has not answered; only an
   * explicit false locks UI, since the API stays the real gate.
   */
  includesOutreach?: boolean;
  includesAiPrescreen?: boolean;
  /** The plan's sourcing allowance, when Stigg counts one. */
  sourcingLimit?: number;
}

/** A paid capability a plan may lack; `automation` is outreach or AI pre-screening. */
export type PlanFeature = 'outreach' | 'automation';

/**
 * Whether the plan explicitly lacks a feature. Without one, whether it is the
 * limited Free plan (interview coordination stays paid). Unknown counts as
 * included, since the API stays the real gate.
 */
export const lacksFeature = (
  state: Pick<
    WorkspacePlanState,
    'plan' | 'includesOutreach' | 'includesAiPrescreen'
  >,
  feature?: PlanFeature,
) => {
  if (feature === 'outreach') return state.includesOutreach === false;
  if (feature === 'automation') {
    return (
      state.includesOutreach === false && state.includesAiPrescreen === false
    );
  }
  return state.plan === 'free';
};

/** Translate the selected workspace's subscription without inventing a fallback plan. */
export const toWorkspacePlan = ({
  planId,
  isLoading,
  error,
}: {
  planId?: string;
  isLoading: boolean;
  error?: Error | null;
}): WorkspacePlanState => {
  if (error) return { plan: undefined, isLoading: false, error };
  if (isLoading) return { plan: undefined, isLoading: true };

  const plan = isLimitedFreePlan(planId)
    ? 'free'
    : planId === 'plan-finlay-source'
      ? 'source'
      : planId === 'plan-finlay-managed'
        ? 'managed'
        : undefined;

  return plan
    ? { plan, isLoading: false }
    : {
        plan: undefined,
        isLoading: false,
        error: new Error('Workspace plan could not be resolved.'),
      };
};
